import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, FileText, ChevronRight, AlertTriangle } from "lucide-react";

import PageHeader from "@/components/PageHeader";
import ClientSearchInput from "@/components/ClientSearchInput";
import PhotoCapture from "@/components/PhotoCapture";
import SignaturePad from "@/components/SignaturePad";
import SavedEmailsPicker from "@/components/SavedEmailsPicker";
import { operationsApi, adminApi } from "@/lib/api";
import { queueOperation } from "@/lib/syncQueue";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { buildOperationHtml } from "@/lib/pdfTemplate";

// Strip the "data:image/...;base64," prefix
const stripDataUrl = (s = "") => s.replace(/^data:[^;]+;base64,/, "");

const usageMap = {
  DEPOT: "BORDEREAU_DEPOT",
  RESTITUTION: "FORM_RESTITUTION",
  DELIVRANCE: "FORM_DELIVRANCE",
};

const subjectMap = {
  DEPOT: "Bordereau de dépôt",
  RESTITUTION: "Bordereau de restitution",
  DELIVRANCE: "Bordereau de délivrance",
};

const backupEmailFieldMap = {
  DEPOT: "email_depot",
  RESTITUTION: "email_restitution",
  DELIVRANCE: "email_delivrance",
};

export default function OperationForm({ type }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const branding = useBranding();

  const [client, setClient] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [signature, setSignature] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [observations, setObservations] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [createdOp, setCreatedOp] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [sendToClient, setSendToClient] = useState(false);
  const [extraEmails, setExtraEmails] = useState([]); // selected saved emails
  const [sendingEmail, setSendingEmail] = useState(false);

  // Backup email config (archive destination) — pre-filled in the recipient field
  const [archiveEmail, setArchiveEmail] = useState("");
  const [archiveEmailLoading, setArchiveEmailLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .backupEmails()
      .then((data) => {
        if (cancelled) return;
        const field = backupEmailFieldMap[type];
        setArchiveEmail(data?.[field] || "");
      })
      .catch(() => {
        if (!cancelled) setArchiveEmail("");
      })
      .finally(() => !cancelled && setArchiveEmailLoading(false));
    return () => {
      cancelled = true;
    };
    // `adminApi` is an imported singleton, `backupEmailFieldMap` is a
    // module-level const, and the setters are stable — `type` is the only
    // value that should trigger a re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const canSubmit = client && photos.length >= 1 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);

    const medias = photos.map((src) => ({
      type: "PHOTO",
      usage: usageMap[type] || "BORDEREAU_DEPOT",
      base64_data: stripDataUrl(src),
    }));
    if (signature) {
      medias.push({
        type: "SIGNATURE",
        usage: usageMap[type] || "BORDEREAU_DEPOT",
        base64_data: stripDataUrl(signature),
      });
    }

    try {
      const body = {
        type,
        client_id: client.id,
        utilisateur: user?.username || "",
        observations: observations || null,
        signatory_name: signatoryName || null,
        medias,
      };
      const op = await operationsApi.create(body);
      setCreatedOp(op);
      setEmailRecipient(archiveEmail || "");
      // Client recipient must be checked manually by the user (no auto-tick).
      setSendToClient(false);
      setExtraEmails([]);
      toast.success(`${subjectMap[type]} créé`, {
        description: `N° ${op.id}`,
      });
    } catch (err) {
      // If we're offline OR the network just failed, queue the operation
      const isNetworkErr =
        !navigator.onLine ||
        err?.code === "ERR_NETWORK" ||
        err?.code === "ECONNABORTED" ||
        !err?.response;
      if (isNetworkErr) {
        try {
          await queueOperation({
            body: {
              type,
              client_id: client.id,
              utilisateur: user?.username || "",
              observations: observations || null,
              signatory_name: signatoryName || null,
              medias,
            },
            client_snapshot: {
              nom: client.nom,
              prenom: client.prenom,
              email: client.email,
            },
            type,
          });
          toast.success("Bordereau enregistré hors ligne", {
            description: "Il sera envoyé automatiquement au retour de la connexion.",
            duration: 5000,
          });
          // Reset the form so the user can start a new one
          setClient(null);
          setPhotos([]);
          setSignature("");
          setSignatoryName("");
          setObservations("");
        } catch (queueErr) {
          toast.error("Impossible d'enregistrer hors ligne", {
            description: queueErr?.message || "Erreur IndexedDB",
          });
        }
      } else {
        toast.error("Erreur lors de la création", {
          description: err?.response?.data?.detail || err.message,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Aggregated recipients list (archive + client + saved extras) for the success screen.
  const recipientsList = (() => {
    const all = [];
    if (emailRecipient) all.push(emailRecipient.trim());
    if (sendToClient && client?.email) all.push(client.email.trim());
    for (const em of extraEmails) {
      if (em && em.trim()) all.push(em.trim());
    }
    // dedupe (case-insensitive)
    const seen = new Set();
    return all.filter((r) => {
      if (!r) return false;
      const k = r.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  })();

  const handleSendEmail = async () => {
    if (!createdOp || recipientsList.length === 0) return;
    setSendingEmail(true);
    try {
      const html = buildOperationHtml({
        operation: createdOp,
        client,
        photos,
        signature,
        branding,
        utilisateur: user?.username,
      });
      const subject = `${subjectMap[type]} - ${client.nom}${client.prenom ? " " + client.prenom : ""} - N° ${createdOp.id}`;
      await operationsApi.sendEmail(createdOp.id, {
        html,
        recipients: recipientsList,
        subject,
        operation_type: type,
        client_name: `${client.nom}${client.prenom ? " " + client.prenom : ""}`,
        sender_username: user?.username,
      });
      toast.success("Email envoyé avec succès", {
        description:
          recipientsList.length === 1
            ? `Le PDF a été envoyé à ${recipientsList[0]}`
            : `Le PDF a été envoyé à ${recipientsList.length} destinataires`,
      });
      navigate(`/operations/${createdOp.id}`, { replace: true });
    } catch (err) {
      toast.error("Erreur lors de l'envoi", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSendingEmail(false);
    }
  };

  if (createdOp) {
    const archiveLabel = {
      DEPOT: "Archive Dépôt",
      RESTITUTION: "Archive Restitution",
      DELIVRANCE: "Archive Délivrance",
    }[type];
    return (
      <div data-testid="operation-success-view">
        <PageHeader
          title={`${subjectMap[type]} créé`}
          subtitle={`N° ${createdOp.id}`}
          backTo="/depot"
        />
        <div className="px-4 md:px-8 py-6 max-w-2xl space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
            <FileText className="w-5 h-5 text-[var(--vidoc-success)] mt-0.5" />
            <div>
              <div className="font-semibold text-[var(--vidoc-success)]">
                Opération enregistrée
              </div>
              <div className="text-sm text-[var(--vidoc-text-secondary)]">
                {photos.length} photo(s) · client {client?.nom} {client?.prenom}
              </div>
            </div>
          </div>

          {!archiveEmail && !archiveEmailLoading && (
            <div
              data-testid="archive-email-warning"
              className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-[var(--vidoc-warning)] mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-orange-900">
                  Email d'archive non configuré
                </div>
                <div className="text-[var(--vidoc-text-secondary)]">
                  Aucun email de sauvegarde pour les {subjectMap[type].toLowerCase()}s n'est défini. Configurez-le dans <strong>Profil → Gérer les sauvegardes</strong> pour automatiser l'envoi.
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <label className="text-sm font-semibold">
              Destinataires du PDF
            </label>

            {/* 1) Archive backup recipient (pre-filled, editable) */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-[var(--vidoc-text-secondary)]">
                {archiveLabel} — destinataire principal
              </label>
              <input
                data-testid="email-recipient-input"
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder={archiveEmail || "archives@vaevidoc.com"}
                className="w-full px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
              />
              <p className="text-xs text-[var(--vidoc-text-tertiary)]">
                Pré-rempli depuis <strong>{archiveLabel}</strong> (Profil → Gérer les sauvegardes). Modifiable au cas par cas.
              </p>
            </div>

            {/* 2) Send to client's email */}
            <label
              data-testid="email-send-to-client-row"
              className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${
                client?.email
                  ? "bg-[var(--vidoc-bg-secondary)] border-[var(--vidoc-border)] cursor-pointer"
                  : "bg-[var(--vidoc-bg-secondary)]/40 border-dashed border-[var(--vidoc-border)] opacity-70"
              }`}
            >
              <input
                data-testid="email-send-to-client-toggle"
                type="checkbox"
                checked={sendToClient && Boolean(client?.email)}
                disabled={!client?.email}
                onChange={(e) => setSendToClient(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[var(--vidoc-primary)] touch-target"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">
                  Envoyer aussi au client
                </div>
                <div className="text-xs text-[var(--vidoc-text-secondary)] truncate">
                  {client?.email
                    ? `${client.email} — cochez si vous souhaitez l'inclure`
                    : "Aucun email enregistré sur la fiche client"}
                </div>
              </div>
            </label>

            {/* 3) Saved additional emails (ADV, CP, comptable…) — built-in master toggle */}
            <SavedEmailsPicker
              selectedEmails={extraEmails}
              onSelectionChange={setExtraEmails}
            />

            {/* Summary of recipients */}
            <div
              data-testid="email-recipients-summary"
              className="text-xs text-[var(--vidoc-text-secondary)] px-1"
            >
              {recipientsList.length === 0
                ? "Aucun destinataire — renseignez au moins une adresse"
                : `Le PDF sera envoyé à : ${recipientsList.join(", ")}`}
            </div>

            <button
              data-testid="send-email-btn"
              type="button"
              onClick={handleSendEmail}
              disabled={recipientsList.length === 0 || sendingEmail}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)]"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {recipientsList.length > 1
                ? `Générer & envoyer le PDF (${recipientsList.length} destinataires)`
                : "Générer & envoyer le PDF"}
            </button>
          </div>

          <div className="flex gap-2">
            <button
              data-testid="new-operation-btn"
              type="button"
              onClick={() => {
                setCreatedOp(null);
                setClient(null);
                setPhotos([]);
                setSignature("");
                setSignatoryName("");
                setObservations("");
              }}
              className="flex-1 px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] font-medium touch-target hover:bg-[var(--vidoc-bg-tertiary)]"
            >
              Nouvelle opération
            </button>
            <button
              data-testid="view-operation-btn"
              type="button"
              onClick={() => navigate(`/operations/${createdOp.id}`)}
              className="flex-1 px-4 py-3 rounded-xl border border-[var(--vidoc-border)] font-medium touch-target hover:bg-[var(--vidoc-bg-secondary)] flex items-center justify-center gap-2"
            >
              Voir détail
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`operation-form-${type.toLowerCase()}`}>
      <PageHeader
        title={subjectMap[type]}
        subtitle="Sélectionnez un client, ajoutez des photos et une signature"
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-6">
        <Section title="Client" required>
          <ClientSearchInput
            value={client}
            onChange={setClient}
            onCreateNew={() => navigate("/clients/add")}
          />
        </Section>

        <Section title="Photos" required hint="Au moins 1 photo">
          <PhotoCapture photos={photos} onChange={setPhotos} />
        </Section>

        <Section title="Observations">
          <textarea
            data-testid="observations-input"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={4}
            placeholder="Description des objets, conditions particulières..."
            className="w-full px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none resize-none"
          />
        </Section>

        <Section title="Signataire (nom complet)">
          <input
            data-testid="signatory-name-input"
            type="text"
            value={signatoryName}
            onChange={(e) => setSignatoryName(e.target.value)}
            placeholder="Optionnel — si différent du client"
            className="w-full px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
          />
        </Section>

        <Section title="Signature">
          <SignaturePad value={signature} onChange={setSignature} />
        </Section>

        <div className="sticky bottom-20 md:bottom-4 pt-10 mt-6">
          <button
            data-testid="submit-operation-btn"
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)] shadow-lg shadow-blue-500/20"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Valider {subjectMap[type].toLowerCase()}
          </button>
          {!canSubmit && !submitting && photos.length === 0 && client && (
            <p className="mt-2 text-xs text-center text-[var(--vidoc-text-secondary)]">
              Ajoutez au moins une photo
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, required, hint, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold">
          {title}
          {required && <span className="text-[var(--vidoc-danger)] ml-1">*</span>}
        </label>
        {hint && <span className="text-xs text-[var(--vidoc-text-secondary)]">{hint}</span>}
      </div>
      {children}
    </section>
  );
}
